import React, { useState } from 'react';
import { CornerDownRight, Trash2 } from 'lucide-react';
import CommentForm from './CommentForm';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, deleteDoc } from 'firebase/firestore';

const Comment = ({ comment, postAuthorId, postId, onReply }) => {
    const { currentUser } = useAuth();
    const [showReplyForm, setShowReplyForm] = useState(false);
    const isOriginalPoster = comment.authorId === postAuthorId;
    const isCommentAuthor = currentUser && comment.authorId === currentUser.uid;
    const authorProfile = comment.authorProfile || {};

    const handleDelete = async () => {
        if (!isCommentAuthor) return;
        try {
            await deleteDoc(doc(db, 'comments', comment.id));
        } catch (e) {
            console.error("Delete comment error:", e);
        }
    };

    return (
        <div className="group animate-fade-in my-2">
            <div className={`flex items-start gap-3 p-3.5 rounded-2xl transition-all ${
                isOriginalPoster
                    ? 'bg-brand-50/50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/25'
                    : 'bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800/80'
            }`}>
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full p-[1px] bg-gradient-to-tr from-brand-500 to-indigo-500 flex-shrink-0">
                    <img
                        src={authorProfile.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${comment.authorId}`}
                        alt=""
                        className="w-full h-full rounded-full object-cover bg-white dark:bg-slate-900"
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-xs text-slate-900 dark:text-slate-100">
                                {authorProfile.name || 'User'}
                            </span>
                            {isOriginalPoster && (
                                <span className="text-[10px] bg-brand-50 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 font-semibold px-1.5 py-0.2 rounded-full border border-brand-200 dark:border-brand-500/30">
                                    Author
                                </span>
                            )}
                            <span className="text-slate-400 dark:text-slate-500 text-[10px]">
                                • {comment.createdAt?.toDate ? comment.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'just now'}
                            </span>
                        </div>

                        {isCommentAuthor && (
                            <button
                                onClick={handleDelete}
                                title="Delete comment"
                                className="text-slate-400 hover:text-rose-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <p className="text-slate-700 dark:text-slate-200 text-xs leading-relaxed whitespace-pre-wrap font-normal">
                        {comment.text}
                    </p>

                    <button
                        onClick={() => setShowReplyForm(!showReplyForm)}
                        className="text-[11px] font-semibold mt-2 text-slate-500 dark:text-slate-400 hover:text-brand-500 dark:hover:text-brand-400 flex items-center gap-1 transition-colors"
                    >
                        <CornerDownRight className="w-3 h-3" />
                        <span>Reply</span>
                    </button>
                </div>
            </div>

            {showReplyForm && (
                <div className="ml-6 sm:ml-8 mt-2 animate-slide-up">
                    <CommentForm
                        postId={postId}
                        parentCommentId={comment.id}
                        onCommentPosted={() => setShowReplyForm(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default Comment;